import { MAX_ANIMATION_SECONDS, SCENE_COORD_LIMIT } from '../domain/schemas';
import { MAX_REPEAT, type TemplateId } from '../animation/procedural/spec';
import type { Command, ParseError } from './commands';

/**
 * Deterministic choreography grammar (Phase 3C). A bounded set of phrasings is parsed into
 * typed `motion.step` commands (template, target reference, timing, duration, repetitions,
 * direction, distance, positions); the executor groups the steps of one prompt into a
 * motion plan. This is not conversational understanding and not neural text-to-animation:
 * anything outside the grammar is rejected with suggestions, never guessed.
 *
 * Accepted shapes (case-insensitive; numbers as digits or words like "three", "twice"):
 *  - "make|have|let <target> <motion> [modifiers]"  — make the selected character run right for 3 seconds
 *  - "<motion> [modifiers]"                         — jump at frame 48 · then wave (same target as before)
 *  - "fade <target> in|out [over …]"                — fade the title in over one second
 *  - "move <target> from x 100 to x 500"            — absolute slide
 *  - "<target> follow(s) this path" / "make <target> follow the path"
 *  - "repeat the <motion> four times", "start the <motion> at 2 seconds"
 *  - "create a six-second animation with running and jumping"
 *  - "apply the motion plan"
 * Modifiers: left / right / in place, "for|over N seconds|frames", "at frame N", "at N
 * seconds", "after N seconds", "N times" / "twice", "N pixels", "at the same time" /
 * "meanwhile", "afterwards", "replacing existing keys".
 */
type Result = { command: Command } | { commands: Command[] } | { error: ParseError } | null;

export type TargetRef = Extract<Command, { type: 'motion.step' }>['target'];

const NUMBER_WORDS: Record<string, number> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
  fifteen: 15, twenty: 20, thirty: 30, half: 0.5,
};
export const NUMBER = String.raw`(\d+(?:\.\d+)?|a|an|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|half(?: a)?)`;
export const toNumber = (raw: string): number => (/^\d/.test(raw) ? Number(raw) : (NUMBER_WORDS[raw.replace(/ a$/, '')] ?? Number.NaN));

/** Verb forms → template. Order matters: longer phrases first. */
const VERBS: [RegExp, TemplateId][] = [
  [/^(?:fall(?:s|ing)? (?:over|down)|fall(?:s|ing)?|topple(?:s)?|trip(?:s)?)\b/, 'fall'],
  [/^(?:turn(?:s|ing)? around|turn(?:s|ing)?(?! (?:up|down|on|off)\b))\b/, 'turn'],
  [/^(?:run(?:s|ning)?|jog(?:s|ging)?|sprint(?:s|ing)?)\b/, 'run'],
  [/^(?:walk(?:s|ing)?|stroll(?:s)?)\b/, 'walk'],
  [/^(?:jump(?:s|ing)?|hop(?:s|ping)?|leap(?:s|ing)?)\b/, 'jump'],
  [/^(?:land(?:s|ing)?)\b/, 'land'],
  [/^(?:crouch(?:es|ing)?|duck(?:s|ing)?|squat(?:s|ting)?)\b/, 'crouch'],
  [/^(?:wave(?:s)?|waving)\b/, 'wave'],
  [/^(?:celebrate(?:s)?|celebrating|cheer(?:s|ing)?)\b/, 'celebrate'],
  [/^(?:idle|breathe(?:s)?|breathing|rest(?:s|ing)?)\b/, 'idle'],
  [/^(?:float(?:s|ing)?|hover(?:s|ing)?)\b/, 'float'],
  [/^(?:bounce(?:s)?|bouncing)\b/, 'bounce'],
  [/^(?:spin(?:s|ning)?)\b/, 'spin'],
  [/^(?:shake(?:s)?|shaking|wobble(?:s)?|wobbling|tremble(?:s)?)\b/, 'shake'],
  [/^(?:pulse(?:s)?|pulsing|pulsate(?:s)?|throb(?:s|bing)?)\b/, 'pulse'],
  // ("swing" is the music grammar's swing amount.)
  [/^(?:sway(?:s|ing)?)\b/, 'sway'],
  [/^(?:fade(?:s)? in|fading in|appear(?:s)?)\b/, 'fadeIn'],
  [/^(?:fade(?:s)? out|fading out|disappear(?:s)?|vanish(?:es)?)\b/, 'fadeOut'],
  [/^(?:follow(?:s|ing)? (?:this|the|my) path|move(?:s)? along (?:this|the|my) path)\b/, 'path'],
];
/** Noun forms ("the bounce", "the floating motion") for repeat/start phrases. */
export const NOUNS: Record<string, TemplateId> = {
  run: 'run', running: 'run', walk: 'walk', walking: 'walk', jump: 'jump', jumping: 'jump', landing: 'land', crouch: 'crouch', wave: 'wave', waving: 'wave',
  turn: 'turn', celebration: 'celebrate', fall: 'fall', idle: 'idle', breathing: 'idle', float: 'float', floating: 'float', bounce: 'bounce', bouncing: 'bounce',
  spin: 'spin', spinning: 'spin', shake: 'shake', shaking: 'shake', pulse: 'pulse', pulsing: 'pulse', sway: 'sway', swaying: 'sway', fade: 'fadeIn', path: 'path',
};

export function verbAt(text: string): { template: TemplateId; rest: string } | null {
  for (const [re, template] of VERBS) {
    const m = text.match(re);
    if (m) return { template, rest: text.slice(m[0].length).trim() };
  }
  return null;
}

/** Parses a target phrase. */
export function parseTarget(phrase: string): TargetRef | null {
  const t = phrase.trim();
  if (/^(?:it|them|they|he|she)$/.test(t)) return { kind: 'inherit' };
  if (/^(?:the |this |that )?selected (?:character|hero|person|figure)s?$/.test(t)) return { kind: 'selected-character' };
  if (/^(?:the |this |that )?(?:selected )?(?:object|shape|item|selection|objects|shapes|items)$/.test(t) || /^(?:the |this |that )?selected$/.test(t)) return { kind: 'selected' };
  if (/^(?:the |this |that |our |my )?(?:character|hero|explorer|player|figure|person)$/.test(t)) return { kind: 'character' };
  const named = t.match(/^(?:the |this |that )?([a-z0-9][a-z0-9 .'-]{0,58})$/);
  if (named && !/^(?:and|then|to|for|at|over|in|by|from)\b/.test(named[1]!)) return { kind: 'name', name: named[1]!.trim() };
  return null;
}

type Step = Extract<Command, { type: 'motion.step' }>;

const err = (segment: string, message: string, example: string): Result => ({ error: { kind: 'invalid-value', message, segment, suggestions: [example] } });

/**
 * Parses modifiers after the motion word. Returns the step fields, or an error naming the
 * words that were not understood (nothing is guessed).
 */
function modifiers(segment: string, template: TemplateId, rest: string, base: Omit<Step, 'type' | 'template'>): Result {
  const step: Step = { type: 'motion.step', template, ...base };
  let text = ` ${rest} `;
  const take = (re: RegExp): RegExpMatchArray | null => {
    const m = text.match(re);
    if (m) text = text.replace(m[0], ' ');
    return m;
  };
  let m: RegExpMatchArray | null;
  const seconds = (raw: string, unit: string): { frames: number } | { seconds: number } => {
    const v = toNumber(raw);
    return /^f/.test(unit) ? { frames: v } : { seconds: /^m/.test(unit) ? v / 1000 : v };
  };
  const UNIT = String.raw`(seconds?|secs?|s|frames?|milliseconds?|ms)`;
  if ((m = take(new RegExp(String.raw` replacing (?:the )?existing (?:keys|keyframes|motion) `)))) step.replace = true;
  if ((m = take(new RegExp(String.raw` (?:for|over|lasting|during|in) ${NUMBER}[ -]?${UNIT} `)))) {
    const d = seconds(m[1]!, m[2]!);
    if ('frames' in d) step.durationFrames = d.frames;
    else step.duration = d.seconds;
  }
  if ((m = take(new RegExp(String.raw` (?:at|from|on|starting at|beginning at) frame (\d+) `)))) step.timing = { mode: 'at', at: null, atFrame: Number(m[1]), delay: 0 };
  else if ((m = take(new RegExp(String.raw` (?:at|starting at|beginning at|after) ${NUMBER}[ -]?${UNIT} `)))) {
    const d = seconds(m[1]!, m[2]!);
    step.timing = 'frames' in d ? { mode: 'at', at: null, atFrame: d.frames, delay: 0 } : { mode: 'at', at: d.seconds, atFrame: null, delay: 0 };
  } else if (take(/ (?:at the same time|meanwhile|simultaneously|in parallel|alongside|while that happens) /)) step.timing = { mode: 'with', at: null, atFrame: null, delay: 0 };
  else if (take(/ (?:afterwards|after that|at the end|next) /)) step.timing = { mode: 'sequence', at: null, atFrame: null, delay: 0 };
  if ((m = take(new RegExp(String.raw` (?:${NUMBER} times|(twice)|(thrice)|once) `)))) step.repeat = m[2] ? 2 : m[3] ? 3 : m[1] ? toNumber(m[1]) : 1;
  if ((m = take(new RegExp(String.raw` (?:by |for )?(\d+(?:\.\d+)?) ?(?:pixels?|px) `)))) step.params.distance = Number(m[1]);
  if ((m = take(/ from x (-?\d+(?:\.\d+)?) /))) step.params.fromX = Number(m[1]);
  if ((m = take(/ from y (-?\d+(?:\.\d+)?) /))) step.params.fromY = Number(m[1]);
  if ((m = take(/ to x (-?\d+(?:\.\d+)?) /))) step.params.toX = Number(m[1]);
  if ((m = take(/ to y (-?\d+(?:\.\d+)?) /))) step.params.toY = Number(m[1]);
  if ((m = take(/ with (?:the )?(left|right) (?:arm|hand) /))) step.params.arm = m[1]!;
  if ((m = take(/ (?:to the |towards the |toward the )?(left|right|up|down)(?:wards)? /))) step.params.direction = m[1]!;
  else if (take(/ in place /)) step.params.direction = 'none';
  if ((m = take(/ (counter-?clockwise|anti-?clockwise|clockwise) /))) step.params.direction = m[1]!.startsWith('c') && !m[1]!.startsWith('counter') ? 'clockwise' : 'counterclockwise';
  take(/ (?:again|motion|animation|cycle) /);
  const leftover = text.trim();
  if (leftover) {
    return {
      error: {
        kind: 'unsupported',
        message: `I understood “${segment.slice(0, segment.length - leftover.length).trim() || segment}” but not “${leftover}”. Choreography prompts accept directions, durations ("for 3 seconds"), start times ("at frame 48", "at 2 seconds"), repetitions ("three times") and distances ("300 pixels").`,
        segment,
        suggestions: ['make the selected character run right for 3 seconds', 'jump at frame 48', 'make the neon sign pulse three times'],
      },
    };
  }
  // Values are checked here so the error names the phrase.
  if (step.repeat !== undefined && !(Number.isInteger(step.repeat) && step.repeat >= 1 && step.repeat <= MAX_REPEAT)) return err(segment, `Repeat 1–${MAX_REPEAT} times.`, 'repeat the bounce four times');
  if (step.duration !== undefined && !(step.duration > 0 && step.duration <= MAX_ANIMATION_SECONDS)) return err(segment, `A motion can last 0–${MAX_ANIMATION_SECONDS} seconds.`, 'make it run for 3 seconds');
  if (step.timing?.at !== null && step.timing?.at !== undefined && !(step.timing.at >= 0 && step.timing.at <= MAX_ANIMATION_SECONDS)) return err(segment, `Start between 0 and ${MAX_ANIMATION_SECONDS} seconds.`, 'start the floating motion at 2 seconds');
  for (const k of ['distance', 'fromX', 'fromY', 'toX', 'toY'] as const) {
    const v = step.params[k];
    if (typeof v === 'number' && Math.abs(v) > SCENE_COORD_LIMIT) return err(segment, `Positions and distances are limited to ±${SCENE_COORD_LIMIT} px.`, 'move the selected object from x 100 to x 500');
  }
  if (template === 'slide' || step.params.toX !== undefined || step.params.toY !== undefined) {
    step.template = 'slide';
    if (step.params.toX !== undefined || step.params.toY !== undefined) step.params.mode = 'absolute';
  }
  // Direction words only where the motion has a direction.
  if (typeof step.params.direction === 'string') {
    const allowed: Partial<Record<TemplateId, string[]>> = {
      walk: ['left', 'right', 'none'],
      run: ['left', 'right', 'none'],
      jump: ['left', 'right', 'none'],
      fall: ['left', 'right'],
      slide: ['left', 'right', 'up', 'down'],
      spin: ['clockwise', 'counterclockwise'],
    };
    if (!allowed[step.template]?.includes(step.params.direction)) {
      return err(segment, `“${step.params.direction === 'none' ? 'in place' : step.params.direction}” does not apply to this motion.`, 'make the selected character run right for 3 seconds');
    }
  }
  if (step.params.distance !== undefined && !['walk', 'run', 'jump', 'slide'].includes(step.template)) return err(segment, 'Only walking, running, jumping and sliding move by a distance.', 'make the character walk right 300 pixels');
  return { command: step };
}

const emptyStep = (target: TargetRef): Omit<Step, 'type' | 'template'> => ({ target, params: {} });

export function parseMotionSegment(segment: string): Result {
  let m: RegExpMatchArray | null;

  if (/^(?:apply|commit|confirm) (?:the |this )?(?:motion )?(?:plan|choreography)$/.test(segment)) return { command: { type: 'motion.apply' } };

  // "create a six-second animation [at 30 fps] with running and jumping"
  if ((m = segment.match(new RegExp(String.raw`^(?:create|make) (?:a |an )?(?:new )?${NUMBER}[ -](?:second|sec|s)s?(?: long)? animation(?: at (\d+) ?fps)?(?: (?:with|featuring|where (?:the |a )?(?:character|hero) (?:does )?) (.+))?$`)))) {
    const seconds = toNumber(m[1]!);
    if (!(seconds > 0 && seconds <= MAX_ANIMATION_SECONDS)) return err(segment, `An animation can last 0–${MAX_ANIMATION_SECONDS} seconds.`, 'create a six-second animation with running and jumping');
    const fps = m[2] !== undefined ? Number(m[2]) : undefined;
    if (fps !== undefined && ![12, 24, 30, 60].includes(fps)) return err(segment, 'Frame rate must be 12, 24, 30 or 60 fps.', 'create a 6-second animation at 24 fps');
    const create: Command = { type: 'anim.create', seconds, ...(fps !== undefined ? { fps: fps as 12 | 24 | 30 | 60 } : {}) };
    if (!m[3]) return /^\d/.test(m[1]!) ? null : { command: create }; // digits: the Phase 3B grammar handles it
    const parts = m[3].split(/\s*(?:,|\band then\b|\bthen\b|\band\b)\s*/).filter(Boolean);
    const steps: Command[] = [];
    for (const part of parts) {
      const v = verbAt(part);
      if (!v) return err(segment, `“${part}” is not a motion I know. Motions: run, walk, jump, land, crouch, wave, turn, celebrate, fall, idle, float, bounce, spin, shake, pulse, sway, fade in/out.`, 'create a six-second animation with running and jumping');
      const r = modifiers(segment, v.template, v.rest, { ...emptyStep(steps.length ? { kind: 'inherit' } : { kind: 'character' }), planSeconds: seconds });
      if (!r || 'error' in r) return r;
      steps.push((r as { command: Command }).command);
    }
    return { commands: [create, ...steps] };
  }

  // "repeat the bounce four times" / "start the floating motion at 2 seconds"
  if ((m = segment.match(/^repeat (?:the |that )?(\w+)(?: motion| animation)? (.+)$/))) {
    const template = NOUNS[m[1]!];
    if (!template) return null;
    return modifiers(segment, template, m[2]!, { ...emptyStep({ kind: 'inherit' }), amend: 'repeat' });
  }
  if ((m = segment.match(/^start (?:the |that )?(\w+)(?: motion| animation)? (.+)$/))) {
    const template = NOUNS[m[1]!];
    if (!template) return null;
    return modifiers(segment, template, m[2]!, { ...emptyStep({ kind: 'inherit' }), amend: 'start' });
  }

  // "fade the title in over one second"
  if ((m = segment.match(/^fade (.+?) (in|out)\b(.*)$/))) {
    const target = parseTarget(m[1]!);
    if (!target) return null;
    return modifiers(segment, m[2] === 'in' ? 'fadeIn' : 'fadeOut', m[3]!, emptyStep(target));
  }

  // "move the selected object from x 100 to x 500"
  if ((m = segment.match(/^(?:move|slide) (.+?) ((?:from|to) [xy] .*)$/))) {
    const target = parseTarget(m[1]!);
    if (!target) return null;
    return modifiers(segment, 'slide', m[2]!, emptyStep(target));
  }

  // "make|have|let <target> <motion> …"
  if ((m = segment.match(/^(?:make|have|let|get) (.+)$/))) {
    const words = m[1]!.split(' ');
    for (let i = 1; i < words.length; i++) {
      const v = verbAt(words.slice(i).join(' '));
      if (!v) continue;
      const target = parseTarget(words.slice(0, i).join(' '));
      if (!target) return null;
      return modifiers(segment, v.template, v.rest, emptyStep(target));
    }
    return null;
  }

  // "<name> follows this path" / "the sign pulses twice"
  if ((m = segment.match(/^(the [a-z0-9 .'-]+?|it|they) ((?:follows?|pulses|bounces|floats|spins|sways|shakes|waves|jumps|runs|walks|lands|falls|turns|crouches|celebrates|fades (?:in|out)|disappears|appears)\b.*)$/))) {
    const v = verbAt(m[2]!);
    const target = parseTarget(m[1]!);
    if (!v || !target) return null;
    return modifiers(segment, v.template, v.rest, emptyStep(target));
  }

  // Bare motion: "jump at frame 48", "then wave", "land at frame 72".
  const v = verbAt(segment);
  // Bare motions take no object ("fade in the selected clip" belongs to the clip grammar).
  if (v && !/^(?:the|this|that|a|an|selected|my)\b/.test(v.rest) && !/^(?:turn (?:on|off)|fade (?:the )?(?:music|audio|track)|run (?:the )?(?:animation|scene|visuals?)$)/.test(segment)) {
    // A bare verb needs something motion-like; plain "spin"/"float" also work (same target as before).
    return modifiers(segment, v.template, v.rest, emptyStep({ kind: 'inherit' }));
  }
  return null;
}

export const isMotionCommand = (c: Command): boolean => c.type.startsWith('motion.');
